/**
 * Persona processing service
 */

import { apiClient } from '../lib/api-client';

export type PersonaOrigin = 'uploaded' | 'prepopulated';

export interface PersonaTraits {
  macroVsMicro: number;
  valueVsGrowth: number;
  timeHorizon: number;
  passiveVsActive: number;
  strategicVsFinancial: number;
}

export interface PersonaData {
  id: string;
  teamName: string;
  segment: 'GDI' | 'TPC' | 'PFA';
  dataOrigin?: PersonaOrigin;
  data_origin?: PersonaOrigin;
  originalFilename: string;
  wordCount: number;
  documentType: string;
  macroVsMicro: number;
  valueVsGrowth: number;
  timeHorizon: number;
  passiveVsActive: number;
  strategicVsFinancial: number;
  questionsAsked: string[];
  keyInsights: string[];
  investmentPhilosophy?: string | null;
  creativityScore: number;
  uniquenessScore: number;
  // Workshop metadata (camelCase)
  tableNumber?: string;
  facilitatorName?: string;
  teamMembers?: string;
  workshopHighlights?: string;
  workshopChallenges?: string;
  workshopUsefulnessRating?: number;
  status: string;
  errorMessage?: string;
  createdAt: string;
  processedAt?: string;
}

export interface ProcessingStatus {
  fileId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'metadata_saved';
  progress: number;
  message: string;
  personaId?: string;
}

export interface PersonaStats {
  totalSubmissions: number;
  avgWordCount: number;
  avgQuestionsAsked: number;
  processedCount: number;
  processingStatus: string;
  aggregateTraits: PersonaTraits;
  segmentBreakdown: Record<string, {
    count: number;
    traits: PersonaTraits;
  }>;
}

export interface InvestorPayload {
  name?: string;
  traits: PersonaTraits;
}

interface ProcessingStatusResponse {
  fileId?: string;
  file_id?: string; // NOTE / TODO: Fix this casing issue throughout - we should not have a mix of camel/snake
  status?: ProcessingStatus['status'];
  progress?: number;
  message?: string;
  personaId?: string;
  persona_id?: string;
}

interface TraitsResponse {
  macroVsMicro?: number;
  macro_vs_micro?: number;
  valueVsGrowth?: number;
  value_vs_growth?: number;
  timeHorizon?: number;
  time_horizon?: number;
  passiveVsActive?: number;
  passive_vs_active?: number;
  strategicVsFinancial?: number;
  strategic_vs_financial?: number;
}

interface SegmentBreakdownResponse {
  count?: number;
  total?: number;
  traits?: TraitsResponse;
}

interface PersonaStatsResponse {
  totalSubmissions?: number;
  total_submissions?: number;
  avgWordCount?: number;
  avg_word_count?: number;
  avgQuestionsAsked?: number;
  avg_questions_asked?: number;
  processedCount?: number;
  processed_count?: number;
  processingStatus?: string;
  processing_status?: string;
  aggregateTraits?: TraitsResponse;
  aggregate_traits?: TraitsResponse;
  segmentBreakdown?: Record<string, SegmentBreakdownResponse>;
  segment_breakdown?: Record<string, SegmentBreakdownResponse>;
}

export class PersonaService {
  private basePath: string;

  constructor(basePath: string = '/personas') {
    const normalized = basePath.startsWith('/') ? basePath : `/${basePath}`;
    this.basePath = normalized.replace(/\/+$/, '');
  }

  private buildPath(path: string = ''): string {
    if (!path) {
      return this.basePath;
    }
    return `${this.basePath}${path.startsWith('/') ? path : `/${path}`}`;
  }
  /**
   * Upload and process a persona document
   */
  async uploadPersona(file: File): Promise<ProcessingStatus> {
    const response = await apiClient.uploadFile<ProcessingStatusResponse>(
      this.buildPath('/upload'),
      file
    );
    return this.normalizeStatus(response);
  }

  /**
   * Get processing status for a file
   */
  async getProcessingStatus(fileId: string): Promise<ProcessingStatus> {
    const response = await apiClient.get<ProcessingStatusResponse>(
      `${this.buildPath('/status')}/${fileId}`
    );
    return this.normalizeStatus(response);
  }

  /**
   * Get aggregate statistics
   */
  async getStats(): Promise<PersonaStats> {
    const raw = await apiClient.get<PersonaStatsResponse>(
      this.buildPath('/stats')
    );
    const normalizeTraits = (traits: TraitsResponse | undefined): PersonaTraits => ({
      macroVsMicro: traits?.macroVsMicro ?? traits?.macro_vs_micro ?? 0,
      valueVsGrowth: traits?.valueVsGrowth ?? traits?.value_vs_growth ?? 0,
      timeHorizon: traits?.timeHorizon ?? traits?.time_horizon ?? 0,
      passiveVsActive: traits?.passiveVsActive ?? traits?.passive_vs_active ?? 0,
      strategicVsFinancial:
        traits?.strategicVsFinancial ?? traits?.strategic_vs_financial ?? 0,
    });

    const aggregateTraits = normalizeTraits(raw?.aggregateTraits ?? raw?.aggregate_traits);

    const segmentBreakdownEntries = (
      Object.entries(
        raw?.segmentBreakdown ?? raw?.segment_breakdown ?? {}
      ) as Array<[string, SegmentBreakdownResponse | undefined]>
    ).map(([segment, value]) => {
      const traits = normalizeTraits(value?.traits);
      return [
        segment,
        {
          count: value?.count ?? value?.total ?? 0,
          traits,
        },
      ];
    });

    return {
      totalSubmissions: raw?.totalSubmissions ?? raw?.total_submissions ?? 0,
      avgWordCount: raw?.avgWordCount ?? raw?.avg_word_count ?? 0,
      avgQuestionsAsked: raw?.avgQuestionsAsked ?? raw?.avg_questions_asked ?? 0,
      processedCount: raw?.processedCount ?? raw?.processed_count ?? 0,
      processingStatus: raw?.processingStatus ?? raw?.processing_status ?? 'idle',
      aggregateTraits,
      segmentBreakdown: Object.fromEntries(segmentBreakdownEntries),
    };
  }

  /**
   * List all personas
   */
  async listPersonas(params?: {
    segment?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ personas: PersonaData[]; total: number }> {
    const response = await apiClient.get<{ personas: PersonaData[]; total: number }>(
      `${this.buildPath('/')}`,
      { params }
    );
    const personas = (response.personas ?? []).map((persona) =>
      this.normalizePersona(persona)
    );

    return {
      personas,
      total: response.total ?? personas.length,
    };
  }

  /**
   * Get a specific persona by ID
   */
  async getPersona(personaId: string): Promise<PersonaData> {
    const response = await apiClient.get<PersonaData>(
      `${this.buildPath()}/${personaId}`
    );
    return this.normalizePersona(response);
  }

  /**
   * Delete a persona
   */
  async deletePersona(personaId: string): Promise<void> {
    await apiClient.delete(`${this.buildPath()}/${personaId}`);
  }

  async getFavouritePersonaForInvestor(
    investor: InvestorPayload
  ): Promise<PersonaData | null> {
    const response = await apiClient.post<PersonaData | null>(
      '/personas/favourite_for_investor',
      investor
    );

    return response ? this.normalizePersona(response) : null;
  }

  private normalizePersona(persona: PersonaData): PersonaData {
    const origin =
      persona.dataOrigin ?? persona.data_origin ?? ('uploaded' as PersonaOrigin);

    return {
      ...persona,
      dataOrigin: origin,
    };
  }

  private normalizeStatus(
    response: ProcessingStatusResponse | null | undefined
  ): ProcessingStatus {
    if (!response) {
      throw new Error('Empty response from persona API');
    }

    const fileId = response.fileId ?? response.file_id;

    if (!fileId) {
      throw new Error('Upload failed: missing file ID');
    }

    return {
      fileId,
      status: (response.status ?? 'processing') as ProcessingStatus['status'],
      progress: response.progress ?? 0,
      message: response.message ?? '',
      personaId: response.personaId ?? response.persona_id,
    };
  }
}

export const personaService = new PersonaService();
export const insightsService = new PersonaService('/insights/personas');
